import {
  BiddingApprovalState,
  BiddingBidSendStatus,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'

/**
 * 「N件の入札を提出」で提出できる状態の札か (AC-BIN-009)。 入札一覧に表示中かは見ない
 * (表示中の札に絞るのは呼び出し元)。
 *
 * 承認軸で決める (送信軸だけでは足りない): backend の状態機械
 * (`intraday_order.go` `RequestApproval` / `Submit`) が受け付けるのは
 * 下書き (DRAFT → REQUEST または SUBMIT) と承認済 (APPROVED → SUBMIT) だけで、
 * 承認申請中 (PENDING) / 差戻し (REJECTED) はどちらのアクションも拒否される。
 * REQUEST は送信軸を立てないため、 送信軸だけで絞ると承認申請済の札が対象に
 * 残り続け、 件数・合計 MW・エリア別内訳が申請済を含んだまま再提出で全件失敗する。
 *
 * 約定軸が失効の札は含めない。 GC で失効にした下書き・承認待ち・差戻しの札は承認軸が
 * N/A (DRAFT) に戻り送信軸も無いので、 約定軸を見ないと下書きと区別できず、 backend が
 * 失効の札の提出を断る (AC-BIN-099、 #11099)。
 */
export const isIntradaySubmittableBid = (bid: IntradayBidRow): boolean =>
  (bid.approvalState === BiddingApprovalState.DRAFT ||
    bid.approvalState === BiddingApprovalState.APPROVED) &&
  bid.bidStatus !== BiddingIntradayOrderStatus.CANCELLED &&
  bid.bidStatus !== BiddingIntradayOrderStatus.AUTO_CANCELLED &&
  bid.bidStatus !== BiddingIntradayOrderStatus.EXPIRED &&
  (bid.sendStatus === undefined ||
    bid.sendStatus === BiddingBidSendStatus.UNSPECIFIED)

/**
 * 「N件の入札を提出」ボタンが押せない原因 (AC-BIN-009)。 押せる時は undefined。
 *
 * ボタンの非活性と、 押せない理由の文 (原因が `hiddenByFilter` の時だけ出す) の両方をこの値で
 * 決める。 絞り込みを解除しても押せるようにならない原因が他にある間は、 解除を促す文を出さない
 * (conventions.md「操作できない時のボタンの扱い」)。 先に並べた原因が優先する。
 */
export type IntradaySubmitDisabledCause =
  | 'readonly'
  | 'beforeAcceptance'
  | 'approvalFlowUnavailable'
  | 'hiddenByFilter'
  | 'noTarget'

export const resolveIntradaySubmitDisabledCause = (input: {
  isReadonly: boolean
  beforeAcceptance: boolean
  isApprovalFlowUnavailable: boolean
  /** 一覧に表示中の、 提出できる下書きの件数。 */
  submitTargetCount: number
  /** 受渡日に、 提出できる下書きがあるか (一覧に表示中かは問わない)。 */
  hasSubmittableBid: boolean
}): IntradaySubmitDisabledCause | undefined => {
  if (input.isReadonly) return 'readonly'
  if (input.beforeAcceptance) return 'beforeAcceptance'
  if (input.isApprovalFlowUnavailable) return 'approvalFlowUnavailable'
  if (input.submitTargetCount > 0) return undefined
  return input.hasSubmittableBid ? 'hiddenByFilter' : 'noTarget'
}
